"use client";

import { c as _c } from "react/compiler-runtime";
import { DollarSign, TrendingUp, Coins } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { MetricCard } from "@/components/dashboard/metric-card";
import { getFeatureDemoCopy } from "@/components/features/feature-demo-copy";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/components/card";
import { mockCostBreakdown, mockMetrics } from "@/lib/mock-feature-data";
import { useI18n } from "@llmgateway/i18n/react";
const COLORS = ["#4f46e5", "#0ea5e9", "#10b981", "#f59e0b"];
function PieTooltipContent(t0) {
  const $ = _c(9);
  const {
    payload
  } = t0;
  if (payload && payload.length) {
    let t1;
    if ($[0] !== payload[0].name) {
      t1 = <p className="font-medium">{payload[0].name}</p>;
      $[0] = payload[0].name;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const t2 = Number(payload[0].value);
    let t3;
    if ($[2] !== t2) {
      t3 = t2.toFixed(2);
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t3) {
      t4 = <p className="text-sm">${t3}</p>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t1 || $[7] !== t4) {
      t5 = <div className="rounded-lg border bg-popover text-popover-foreground p-2 shadow-sm">{t1}{t4}</div>;
      $[6] = t1;
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  return null;
}
export function CostAnalyticsDemo() {
  const $ = _c(103);
  const {
    locale
  } = useI18n();
  let t0;
  if ($[0] !== locale) {
    t0 = getFeatureDemoCopy(locale);
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const copy = t0.costAnalytics;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== copy.avgCostPer1KTokens || $[3] !== copy.blendedRate || $[4] !== copy.costByProvider || $[5] !== copy.costByProviderDescription || $[6] !== copy.inputOutput || $[7] !== copy.last7Days || $[8] !== copy.totalSpend || $[9] !== copy.totalTokens) {
    const pieData = mockCostBreakdown.providers.map(_temp);
    t14 = "space-y-6";
    const t16 = copy.totalSpend;
    let t17;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = mockCostBreakdown.total.toFixed(2);
      $[30] = t17;
    } else {
      t17 = $[30];
    }
    let t18;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <DollarSign className="h-4 w-4" />;
      $[31] = t18;
    } else {
      t18 = $[31];
    }
    let t19;
    if ($[32] !== copy.last7Days || $[33] !== copy.totalSpend) {
      t19 = <MetricCard label={t16} value={`$${t17}`} subtitle={copy.last7Days} icon={t18} accent="green" />;
      $[32] = copy.last7Days;
      $[33] = copy.totalSpend;
      $[34] = t19;
    } else {
      t19 = $[34];
    }
    let t20;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <Coins className="h-4 w-4" />;
      $[35] = t20;
    } else {
      t20 = $[35];
    }
    let t21;
    if ($[36] !== copy.avgCostPer1KTokens || $[37] !== copy.blendedRate) {
      t21 = <MetricCard label={copy.avgCostPer1KTokens} value="$0.106" subtitle={copy.blendedRate} icon={t20} accent="blue" />;
      $[36] = copy.avgCostPer1KTokens;
      $[37] = copy.blendedRate;
      $[38] = t21;
    } else {
      t21 = $[38];
    }
    let t22;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <TrendingUp className="h-4 w-4" />;
      $[39] = t22;
    } else {
      t22 = $[39];
    }
    let t23;
    if ($[40] !== copy.inputOutput || $[41] !== copy.totalTokens) {
      t23 = <MetricCard label={copy.totalTokens} value={mockMetrics.totalTokens} subtitle={copy.inputOutput} icon={t22} accent="purple" />;
      $[40] = copy.inputOutput;
      $[41] = copy.totalTokens;
      $[42] = t23;
    } else {
      t23 = $[42];
    }
    if ($[43] !== t19 || $[44] !== t21 || $[45] !== t23) {
      t15 = <div className="grid gap-4 md:grid-cols-3">{t19}{t21}{t23}</div>;
      $[43] = t19;
      $[44] = t21;
      $[45] = t23;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    t13 = "grid gap-6 md:grid-cols-2";
    T4 = Card;
    let t24;
    if ($[47] !== copy.costByProvider) {
      t24 = <CardTitle>{copy.costByProvider}</CardTitle>;
      $[47] = copy.costByProvider;
      $[48] = t24;
    } else {
      t24 = $[48];
    }
    let t25;
    if ($[49] !== copy.costByProviderDescription) {
      t25 = <CardDescription>{copy.costByProviderDescription}</CardDescription>;
      $[49] = copy.costByProviderDescription;
      $[50] = t25;
    } else {
      t25 = $[50];
    }
    if ($[51] !== t24 || $[52] !== t25) {
      t12 = <CardHeader>{t24}{t25}</CardHeader>;
      $[51] = t24;
      $[52] = t25;
      $[53] = t12;
    } else {
      t12 = $[53];
    }
    T3 = CardContent;
    T2 = ResponsiveContainer;
    t10 = "100%";
    t11 = 300;
    T1 = PieChart;
    T0 = Pie;
    t1 = pieData;
    t2 = "50%";
    t3 = "50%";
    t4 = false;
    t5 = _temp2;
    t6 = 80;
    t7 = "#8884d8";
    t8 = "value";
    t9 = pieData.map(_temp3);
    $[2] = copy.avgCostPer1KTokens;
    $[3] = copy.blendedRate;
    $[4] = copy.costByProvider;
    $[5] = copy.costByProviderDescription;
    $[6] = copy.inputOutput;
    $[7] = copy.last7Days;
    $[8] = copy.totalSpend;
    $[9] = copy.totalTokens;
    $[10] = T0;
    $[11] = T1;
    $[12] = T2;
    $[13] = T3;
    $[14] = T4;
    $[15] = t1;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    T0 = $[10];
    T1 = $[11];
    T2 = $[12];
    T3 = $[13];
    T4 = $[14];
    t1 = $[15];
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    t14 = $[20];
    t15 = $[21];
    t2 = $[22];
    t3 = $[23];
    t4 = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t16;
  if ($[54] !== T0 || $[55] !== t1 || $[56] !== t2 || $[57] !== t3 || $[58] !== t4 || $[59] !== t5 || $[60] !== t6 || $[61] !== t7 || $[62] !== t8 || $[63] !== t9) {
    t16 = <T0 data={t1} cx={t2} cy={t3} labelLine={t4} label={t5} outerRadius={t6} fill={t7} dataKey={t8}>{t9}</T0>;
    $[54] = T0;
    $[55] = t1;
    $[56] = t2;
    $[57] = t3;
    $[58] = t4;
    $[59] = t5;
    $[60] = t6;
    $[61] = t7;
    $[62] = t8;
    $[63] = t9;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  let t17;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Tooltip content={<PieTooltipContent payload={[]} />} />;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== T1 || $[67] !== t16) {
    t18 = <T1>{t16}{t17}</T1>;
    $[66] = T1;
    $[67] = t16;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== T2 || $[70] !== t10 || $[71] !== t11 || $[72] !== t18) {
    t19 = <T2 width={t10} height={t11}>{t18}</T2>;
    $[69] = T2;
    $[70] = t10;
    $[71] = t11;
    $[72] = t18;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  let t20;
  if ($[74] !== T3 || $[75] !== t19) {
    t20 = <T3>{t19}</T3>;
    $[74] = T3;
    $[75] = t19;
    $[76] = t20;
  } else {
    t20 = $[76];
  }
  let t21;
  if ($[77] !== T4 || $[78] !== t12 || $[79] !== t20) {
    t21 = <T4>{t12}{t20}</T4>;
    $[77] = T4;
    $[78] = t12;
    $[79] = t20;
    $[80] = t21;
  } else {
    t21 = $[80];
  }
  let t22;
  if ($[81] !== copy.providerBreakdown) {
    t22 = <CardTitle>{copy.providerBreakdown}</CardTitle>;
    $[81] = copy.providerBreakdown;
    $[82] = t22;
  } else {
    t22 = $[82];
  }
  let t23;
  if ($[83] !== copy.providerBreakdownDescription) {
    t23 = <CardDescription>{copy.providerBreakdownDescription}</CardDescription>;
    $[83] = copy.providerBreakdownDescription;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  let t24;
  if ($[85] !== t22 || $[86] !== t23) {
    t24 = <CardHeader>{t22}{t23}</CardHeader>;
    $[85] = t22;
    $[86] = t23;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  let t25;
  if ($[88] !== copy.ofTotal) {
    t25 = mockCostBreakdown.providers.map((provider, index_0) => <div key={provider.name} className="flex items-center justify-between p-3 rounded-lg border"><div className="flex items-center gap-3"><div className="h-3 w-3 rounded-full" style={{
          backgroundColor: COLORS[index_0 % COLORS.length]
        }} /><div><p className="font-medium">{provider.name}</p><p className="text-sm text-muted-foreground">{provider.percentage.toFixed(1)}% {copy.ofTotal}</p></div></div><div className="text-right"><p className="font-semibold">${provider.cost.toFixed(2)}</p></div></div>);
    $[88] = copy.ofTotal;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== t25) {
    t26 = <CardContent><div className="space-y-3">{t25}</div></CardContent>;
    $[90] = t25;
    $[91] = t26;
  } else {
    t26 = $[91];
  }
  let t27;
  if ($[92] !== t24 || $[93] !== t26) {
    t27 = <Card>{t24}{t26}</Card>;
    $[92] = t24;
    $[93] = t26;
    $[94] = t27;
  } else {
    t27 = $[94];
  }
  let t28;
  if ($[95] !== t13 || $[96] !== t21 || $[97] !== t27) {
    t28 = <div className={t13}>{t21}{t27}</div>;
    $[95] = t13;
    $[96] = t21;
    $[97] = t27;
    $[98] = t28;
  } else {
    t28 = $[98];
  }
  let t29;
  if ($[99] !== t14 || $[100] !== t15 || $[101] !== t28) {
    t29 = <div className={t14}>{t15}{t28}</div>;
    $[99] = t14;
    $[100] = t15;
    $[101] = t28;
    $[102] = t29;
  } else {
    t29 = $[102];
  }
  return t29;
}
function _temp3(entry, index) {
  return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />;
}
function _temp2(props) {
  const {
    name,
    percent
  } = props;
  return `${name} ${((percent as number) * 100).toFixed(0)}%`;
}
function _temp(p) {
  return {
    name: p.name,
    value: p.cost
  };
}